import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Calendar, 
  Image, 
  Video, 
  LogOut, 
  Menu, 
  X,
  Home,
  Settings,
  User,
  Clock,
  MessageSquare,
  TrendingUp,
  FileText,
  Bell,
  Mail,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  PenTool,
  MessageCircle
} from 'lucide-react';
import authService from '../utils/auth';
import config from '../utils/config';
import JoinUsAdmin from './JoinUsAdmin';

const API_URL = config.url;

const AdminLayout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationBlink, setNotificationBlink] = useState(false);
  const [notificationDetails, setNotificationDetails] = useState({ contacts: [], appointments: [] });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuthentication();
    fetchNotificationCounts();
    authService.startTokenCheck();
    // Poll notifications every 10 seconds
    const interval = setInterval(fetchNotificationCounts, 10000);
    return () => {
      authService.stopTokenCheck();
      clearInterval(interval);
    };
  }, [navigate]);

  useEffect(() => {
    // Blink if notification count increases
    if (notificationCount > lastNotificationCount) {
      setNotificationBlink(true);
      setTimeout(() => setNotificationBlink(false), 1200);
    }
    setLastNotificationCount(notificationCount);
  }, [notificationCount]);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      const adminData = localStorage.getItem('adminData');
      
      if (!token) {
        throw new Error('No token found');
      }

      if (authService.isTokenExpired(token)) {
        throw new Error('Token expired');
      }

      // Verify token with server
      const isValid = await authService.verifyToken();
      if (!isValid) {
        throw new Error('Token verification failed');
      }

      if (adminData) {
        setIsAuthenticated(true);
        setAdmin(JSON.parse(adminData));
      } else {
        throw new Error('No admin data found');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setAdmin(null);
    navigate('/admin/login');
  };

  const fetchNotificationCounts = async () => {
    try {
      const token = authService.getToken();
      // Fetch contacts
      const contactsRes = await fetch(`${API_URL}/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const contacts = await contactsRes.json();
      const unread = Array.isArray(contacts) ? contacts.filter(c => c.status === 'unread') : [];
      setUnreadContacts(unread.length);
      // Fetch appointments
      const apptRes = await fetch(`${API_URL}/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let appts = await apptRes.json();
      if (!Array.isArray(appts) && appts && Array.isArray(appts.appointments)) appts = appts.appointments;
      const pending = Array.isArray(appts) ? appts.filter(a => a.status === 'pending') : [];
      setPendingAppointments(pending.length);
      setNotificationCount(unread.length + pending.length);
      // Save details for dropdown
      setNotificationDetails({
        contacts: unread.slice(0, 5),
        appointments: pending.slice(0, 5)
      });
    } catch (e) {
      setUnreadContacts(0);
      setPendingAppointments(0);
      setNotificationCount(0);
      setNotificationDetails({ contacts: [], appointments: [] });
    }
  };

  const menuItems = [
    { name: 'Dashboard', icon: Home, path: '/admin', color: 'text-blue-600' },
    { name: 'Events', icon: Calendar, path: '/admin/events', color: 'text-green-600' },
    { name: 'Gallery', icon: Image, path: '/admin/gallery', color: 'text-purple-600' },
    { name: 'Videos', icon: Video, path: '/admin/videos', color: 'text-red-600' },
    { name: 'Appointments', icon: Clock, path: '/admin/appointments', color: 'text-yellow-600' },
    { name: 'Contacts', icon: MessageSquare, path: '/admin/contacts', color: 'text-indigo-600' },
    { name: 'Comments', icon: MessageCircle, path: '/admin/comments', color: 'text-teal-600' },
    { name: 'Join Us', icon: User, path: '/admin/joinus', color: 'text-orange-600' },
    { name: 'Press', icon: FileText, path: '/admin/press', color: 'text-pink-600' },
    { name: 'Poetry', icon: BookOpen, path: '/admin/rachnaye', color: 'text-teal-600' },
    { name: 'Blog', icon: PenTool, path: '/admin/blog', color: 'text-cyan-600' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 hindi-text">
      {/* Header - Fixed at top */}
      <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50 h-20">
        {/* <div className="container mx-auto px-0 h-full"> */}
          <div className="flex items-center justify-between h-full px-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
                aria-label="Toggle sidebar"
              >
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
              <img src="/images/sidhisochlogo.png" alt="Logo" className="h-10 w-auto" />
              <span className="text-xl font-bold text-orange-600 tracking-wide hidden md:inline-block">Admin Panel</span>
            </div>
            <div className="flex items-center space-x-4 md:space-x-6">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications((v) => !v)}
                  className={`relative group focus:outline-none ${notificationBlink ? 'animate-pulse' : ''}`}
                  aria-label="Show notifications"
                >
                  <Bell className="w-7 h-7 text-orange-600" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold shadow animate-bounce">
                      {notificationCount}
                    </span>
                  )}
                </button>
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Notifications</h4>
                    {notificationCount === 0 && <div className="text-gray-500 text-sm">No new notifications</div>}
                    {notificationDetails.contacts.length > 0 && (
                      <div className="mb-2">
                        <div className="text-xs text-orange-600 font-bold mb-1">New Contacts</div>
                        {notificationDetails.contacts.map((c) => (
                          <div key={c.id} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0">
                            <span className="truncate text-sm text-gray-700">{c.name || c.email}</span>
                            <button className="text-xs text-blue-600 hover:underline ml-2" onClick={() => { setShowNotifications(false); navigate('/admin/contacts'); }}>
                              View
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {notificationDetails.appointments.length > 0 && (
                      <div>
                        <div className="text-xs text-orange-600 font-bold mb-1">Pending Appointments</div>
                        {notificationDetails.appointments.map((a) => (
                          <div key={a.id} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0">
                            <span className="truncate text-sm text-gray-700">{a.name || a.email}</span>
                            <button className="text-xs text-blue-600 hover:underline ml-2" onClick={() => { setShowNotifications(false); navigate('/admin/appointments'); }}>
                              View
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* User Info */}
              <div className="flex items-center space-x-2 bg-orange-50 px-3 py-1 rounded-lg">
                <img src="/images/logo.png" alt="Company Logo" className="h-8 w-8 rounded-full border border-gray-200" />
                <span className="text-sm text-gray-700 font-semibold">{admin?.username}</span>
              </div>
              {/* Logout */}
              <button onClick={handleLogout} className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow transition-all flex items-center space-x-2">
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        {/* </div> */}
      </header>
      
      {/* Main content with padding to account for fixed header */}
      <div className="flex-1 flex overflow-hidden pt-20">
        {/* Sidebar */}
        <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} bg-white shadow-lg fixed left-0 top-20 bottom-0 z-30 border-r border-gray-200 transition-all duration-300 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <nav className={`py-6 space-y-2 flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-1' : 'px-2'}`} style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.name}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} space-x-3 px-2 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${isActive ? 'bg-orange-50 text-orange-700 border-r-2 border-orange-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
              >
                <Icon className={`w-5 h-5 ${item.color}`} />
                  {!sidebarCollapsed && <span>{item.name}</span>}
              </button>
            );
          })}
        </nav>
          {/* Modern Collapse/Expand Arrow */}
          <button
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            className="absolute right-[-14px] top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg w-7 h-7 flex items-center justify-center border-2 border-white z-50 transition-colors"
            style={{ outline: 'none' }}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            tabIndex={0}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
          {/* Developer Credit (only here, bottom of sidebar) */}
          <div className={`${sidebarCollapsed ? 'px-1' : 'px-3'} pb-4`}>
            <div className={`flex ${sidebarCollapsed ? 'flex-col items-center' : 'flex-col items-start'} text-gray-500`}>
              {!sidebarCollapsed && (
                <span className="text-[11px] leading-tight mb-1">Designed & Developed by</span>
              )}
              <img
                src="/images/Group 45.png"
                alt="CODEVERGE"
                className={`${sidebarCollapsed ? 'h- w-20 self-center' : 'h-6 w-20'} rounded-none border-0 bg-transparent`}
                title="CODEVERGE"
              />
            </div>
          </div>
       </aside>
      {/* Main content */}
        <main className={`flex-1 min-h-screen ${sidebarCollapsed ? 'ml-20' : 'ml-64'} flex flex-col transition-all duration-300`}>
        <div className="p-6 flex-1 bg-gray-50 min-h-[calc(100vh-4rem)] pb-28">
          <Outlet />
        </div>
      </main>
      </div>
     </div>
   );
 };

 export default AdminLayout; 